import type { PolymarketBridge, ApiResponse, OpenOrder, Position, TradeActivity } from '../types';

function ensure(): PolymarketBridge | null {
  if (typeof window !== 'undefined' && window.pm) return window.pm;
  return null;
}

function err<T = unknown>(msg: string): ApiResponse<T> { return { ok: false, error: msg }; }

export const pm: PolymarketBridge = {
  async subscribe(payload: { slug: string; session_id?: string }) {
    const b = ensure();
    if (b && typeof b.subscribe === 'function') return b.subscribe(payload || {} as any);
    return web().subscribe(payload || {} as any);
  },
  onStatus(callback: (payload: any) => void) {
    const b = ensure();
    if (b && typeof b.onStatus === 'function') return b.onStatus(callback);
    web().onStatus(callback);
  },
  async refreshOrderbook() {
    const b = ensure();
    try {
      // Prefer selected sub-market from dropdown, then slug input, then last saved
      let slug = '';
      try {
        if (typeof document !== 'undefined') {
          const sel = document.getElementById('marketSelect') as HTMLSelectElement | null;
          const val = sel && sel.value ? String(sel.value).trim() : '';
          if (val) slug = val;
          if (!slug) {
            const inp = document.getElementById('slug') as HTMLInputElement | null;
            const ival = inp && inp.value ? String(inp.value).trim() : '';
            if (ival) slug = ival;
          }
        }
      } catch {}
      if (!slug && typeof localStorage !== 'undefined') {
        const saved = localStorage.getItem('pm_last_slug');
        if (saved) slug = saved;
      }
      if (b && typeof b.subscribe === 'function') return await b.subscribe({ slug } as any);
      return await web().subscribe({ slug } as any);
    } catch (e) {
      const error = e as Error;
      return err(error?.message ?? String(e));
    }
  },
  onOrderbook(callback: (text: string) => void) {
    const b = ensure();
    if (b && typeof b.onOrderbook === 'function') return b.onOrderbook(callback);
    web().onOrderbook(callback);
  },
  async getEventMarkets(payload: { eventSlug: string }) {
    const b = ensure();
    if (b && typeof (b as any).getEventMarkets === 'function') return (b as any).getEventMarkets(payload);
    return web().getEventMarkets(payload);
  },
  async getMarketActivity(opts?: { limit?: number }) {
    const b = ensure();
    if (b && typeof b.getMarketActivity === 'function') return b.getMarketActivity(opts || {} as any);
    return web().getMarketActivity(opts || {} as any);
  },
  async placeOrder(order: any) {
    const b = ensure();
    if (b && typeof b.placeOrder === 'function') return b.placeOrder(order);
    return web().placeOrder(order);
  },
  async getFunds() {
    const b = ensure();
    if (b && typeof b.getFunds === 'function') return b.getFunds();
    return web().getFunds();
  },
  async refreshFunds() {
    const b = ensure();
    if (b && typeof b.refreshFunds === 'function') return b.refreshFunds();
    return web().refreshFunds();
  },
  async listOpenOrders(filters?: { asset_ids?: string[] }) {
    const b = ensure();
    if (b && typeof b.listOpenOrders === 'function') return b.listOpenOrders(filters || {});
    return web().listOpenOrders(filters || {});
  },
  async cancelOrder(orderId: string) {
    const b = ensure();
    if (b && typeof b.cancelOrder === 'function') return b.cancelOrder(orderId);
    return web().cancelOrder(orderId);
  },
  async cancelAll(scope?: { asset_id?: string }) {
    const b = ensure();
    if (b && typeof b.cancelAll === 'function') return b.cancelAll(scope || {});
    return web().cancelAll(scope || {});
  },
  async getPositions(filters?: { asset_ids?: string[] }) {
    const b = ensure();
    if (b && typeof b.getPositions === 'function') return b.getPositions(filters || {});
    return web().getPositions(filters || {});
  },
  log(line: unknown) {
    const b = ensure();
    if (b && typeof b.log === 'function') {
      try { b.log(line); } catch {}
      return;
    }
    try { web().log(line as any); } catch {}
  },
};

function web(): PolymarketBridge {
  if (typeof window === 'undefined') return createWebAdapter();
  if (!window.__pmWebAdapter) (window as any).__pmWebAdapter = createWebAdapter();
  return window.__pmWebAdapter as PolymarketBridge;
}

function createWebAdapter(): PolymarketBridge {
  const WS_URL = 'wss://ws-subscriptions-clob.polymarket.com/ws/market';
  const GAMMA_MARKET_URL = 'https://gamma-api.polymarket.com/markets?slug=';
  const DATA_API_TRADES = 'https://data-api.polymarket.com/trades';
  const GAMMA_EVENT_URL = 'https://gamma-api.polymarket.com/events/slug/';
  // Base URL for hosted backend (Cloudflare Pages Functions when deployed together)
  // Can be overridden by setting window.__PM_API_BASE at runtime
  const API_BASE = (typeof window !== 'undefined' && (window as any).__PM_API_BASE) || '/api';
  const API_KEY = (typeof window !== 'undefined' && (window as any).__PM_API_KEY) || '';

  let ws: WebSocket | null = null;
  let pingTimer: ReturnType<typeof setInterval> | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let backoffMs = 1000;
  const INITIAL_BACKOFF_MS = 1000;
  const MAX_BACKOFF_MS = 30000;
  const JITTER = 0.2;
  let lastSlug = '';
  let lastConditionId = '';
  const statusHandlers: Array<(payload: any) => void> = [];
  const orderbookHandlers: Array<(text: string) => void> = [];

  function emitStatus(payload: any): void {
    try { statusHandlers.forEach((cb) => { try { cb(payload); } catch {} }); } catch {}
  }
  function emitOrderbook(text: string): void {
    try { orderbookHandlers.forEach((cb) => { try { cb(text); } catch {} }); } catch {}
  }
  function closeWs(): void {
    try { if (pingTimer) clearInterval(pingTimer); } catch {}
    pingTimer = null;
    try { if (ws) ws.close(); } catch {}
    ws = null;
  }
  function scheduleReconnect(): void {
    if (!lastSlug) return;
    if (reconnectTimer) return;
    const base = backoffMs;
    const jitter = 1 + (Math.random() * 2 - 1) * JITTER;
    const delay = Math.floor(base * jitter);
    reconnectTimer = setTimeout(async () => {
      reconnectTimer = null;
      try { await subscribeFn({ slug: lastSlug } as any); backoffMs = INITIAL_BACKOFF_MS; }
      catch { backoffMs = Math.min(backoffMs * 2, MAX_BACKOFF_MS); scheduleReconnect(); }
    }, delay);
  }

  async function fetchMarketBySlug(slug: string): Promise<{ tokenIds: string[]; outcomes: string[]; market: any }> {
    // Try proxy first to avoid CORS in browsers; fallback to direct Gamma
    const proxy = await fetch(`${API_BASE}/gamma-markets?slug=${encodeURIComponent(slug)}`).catch(() => null);
    let list: any[] | null = null;
    if (proxy && proxy.ok) {
      const j = await proxy.json().catch(() => null);
      if (j && j.ok && Array.isArray(j.data)) list = j.data as any[];
    }
    if (!list) {
      const res = await fetch(GAMMA_MARKET_URL + encodeURIComponent(slug));
      if (!res.ok) throw new Error('Gamma API ' + res.status);
      list = await res.json() as any[];
    }
    if (!Array.isArray(list) || list.length === 0) throw new Error('Market not found');
    const market = list[0]!;
    const outcomes = Array.isArray(market.outcomes) ? market.outcomes : (() => { try { return JSON.parse(market.outcomes as string || '[]'); } catch { return []; } })();
    let tokenIds: string[];
    if (typeof market.clobTokenIds === 'string') { try { tokenIds = JSON.parse(market.clobTokenIds); } catch { tokenIds = []; } }
    else tokenIds = market.clobTokenIds;
    if (!Array.isArray(tokenIds) || tokenIds.length === 0) throw new Error('No clobTokenIds');
    return { tokenIds, outcomes, market };
  }

  // Small helper to call hosted API endpoints with consistent headers and error shape
  async function apiGet<T = unknown>(path: string): Promise<import('../types').ApiResponse<T>> {
    try {
      const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
      const res = await fetch(url, { headers: API_KEY ? { 'x-api-key': API_KEY } : undefined });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) return { ok: false, error: (json && json.error) || `HTTP ${res.status}` } as any;
      return (json && typeof json.ok === 'boolean') ? json : { ok: true, data: json } as any;
    } catch (e) {
      const err = e as Error;
      return { ok: false, error: err?.message || String(e) } as any;
    }
  }

  async function apiPost<T = unknown>(path: string, body?: any): Promise<import('../types').ApiResponse<T>> {
    try {
      const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
      const headers: Record<string, string> = { 'content-type': 'application/json' };
      if (API_KEY) headers['x-api-key'] = API_KEY;
      const res = await fetch(url, { method: 'POST', headers, body: body ? JSON.stringify(body) : undefined });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) return { ok: false, error: (json && json.error) || `HTTP ${res.status}` } as any;
      return (json && typeof json.ok === 'boolean') ? json : { ok: true, data: json } as any;
    } catch (e) {
      const err = e as Error;
      return { ok: false, error: err?.message || String(e) } as any;
    }
  }

  function connectToMarket(tokenIds: string[], outcomesById: Record<string, string>): void {
    closeWs();
    ws = new WebSocket(WS_URL);
    ws.addEventListener('open', () => {
      try { ws?.send(JSON.stringify({ assets_ids: tokenIds, type: 'market' })); } catch {}
      try { if (pingTimer) clearInterval(pingTimer); } catch {}
      pingTimer = setInterval(() => { try { if (ws && ws.readyState === 1) ws.send('PING'); } catch {} }, 10000);
      emitStatus({ type: 'subscribed', tokenIds, outcomesById });
      backoffMs = INITIAL_BACKOFF_MS;
    });
    ws.addEventListener('message', (ev) => {
      try {
        const text = typeof ev.data === 'string' ? ev.data : (ev.data as any)?.toString ? (ev.data as any).toString() : '';
        if (text === 'PING' || text === 'PONG') { emitStatus({ type: 'heartbeat' }); return; }
        emitOrderbook(text);
      } catch {}
    });
    ws.addEventListener('close', () => { try { if (pingTimer) clearInterval(pingTimer); } catch {}; pingTimer = null; emitStatus({ type: 'closed' }); scheduleReconnect(); });
    ws.addEventListener('error', (e) => { emitStatus({ type: 'error', message: (e as ErrorEvent)?.message || 'WebSocket error' }); try { ws?.close(); } catch {}; scheduleReconnect(); });
  }

  async function subscribeFn(payload: { slug: string }): Promise<ApiResponse<{ tokenIds: string[] }>> {
    try {
      const slug = String(payload?.slug || '').trim();
      if (!slug) return err<{ tokenIds: string[] }>('Missing slug');
      lastSlug = slug;
      const { tokenIds, outcomes, market } = await fetchMarketBySlug(slug);
      const outcomesById: Record<string, string> = {};
      for (let i = 0; i < tokenIds.length; i++) { const id = tokenIds[i]; if (id) outcomesById[id] = outcomes?.[i] || `Outcome ${i}`; }
      lastConditionId = (market?.conditionId || market?.condition_id) || '';
      emitStatus({ type: 'market', question: market?.question || market?.slug || slug, slug, conditionId: lastConditionId, decimalsById: {} });
      connectToMarket(tokenIds, outcomesById);
      return { ok: true, data: { tokenIds } };
    } catch (e) { const error = e as Error; emitStatus({ type: 'error', message: error?.message ?? String(e) }); backoffMs = Math.min(backoffMs * 2, MAX_BACKOFF_MS); return err<{ tokenIds: string[] }>(error?.message ?? String(e)); }
  }

  return {
    async subscribe(payload: { slug: string }) { return subscribeFn(payload); },
    onStatus(cb: (payload: any) => void) { if (typeof cb === 'function') statusHandlers.push(cb); },
    async refreshOrderbook() {
      try {
        // Soft refresh: if WS is healthy, avoid tearing it down
        if (ws && ws.readyState === 1) {
          emitStatus({ type: 'debug', message: 'soft_refresh' });
          return { ok: true } as any;
        }
      } catch {}
      if (!lastSlug) return err('No market selected');
      return subscribeFn({ slug: lastSlug });
    },
    onOrderbook(cb: (text: string) => void) { if (typeof cb === 'function') orderbookHandlers.push(cb); },
    async getEventMarkets(payload: { eventSlug: string }) {
      try {
        const slug = String(payload?.eventSlug || '').trim();
        if (!slug) return err('Missing event slug');
        // Try proxy first to avoid CORS; fallback to direct Gamma
        let json: any | null = null;
        const prox = await fetch(`${API_BASE}/gamma-events?slug=${encodeURIComponent(slug)}`).catch(() => null);
        if (prox && prox.ok) {
          const j = await prox.json().catch(() => null);
          if (j && j.ok && j.data) json = j.data;
        }
        if (!json) {
          const res = await fetch(GAMMA_EVENT_URL + encodeURIComponent(slug));
          if (!res.ok) return { ok: false, error: 'Gamma Events API ' + res.status } as any;
          json = await res.json();
        }
        const eventSlug = String(json?.slug || slug);
        const question = json?.title || json?.question || undefined;
        const rawMarkets = Array.isArray(json?.markets) ? json.markets : [];
        const toArr = (v: any): string[] => Array.isArray(v) ? v : (() => { try { return JSON.parse(String(v || '[]')); } catch { return []; } })();
        const markets = rawMarkets.map((m: any) => ({
          slug: String(m?.slug || ''),
          question: m?.question || m?.slug || '',
          clobTokenIds: toArr(m?.clobTokenIds),
          outcomes: toArr(m?.outcomes),
          orderPriceMinTickSize: m?.orderPriceMinTickSize,
        }));
        return { ok: true, data: { eventSlug, question, markets } } as any;
      } catch (e) { const error = e as Error; return err(error?.message ?? String(e)); }
    },
    async getMarketActivity(opts: { limit?: number }) {
      try {
        const limit = opts?.limit && typeof opts.limit === 'number' && opts.limit > 0 ? Math.min(1000, opts.limit) : 100;
        const conditionId = lastConditionId || '';
        if (!conditionId) return { ok: true, data: [] };
        const url = new URL(DATA_API_TRADES);
        url.searchParams.set('limit', String(limit));
        url.searchParams.set('market', conditionId);
        const res = await fetch(url.toString());
        if (!res.ok) return { ok: false, error: 'Data-API ' + res.status };
        const arr = await res.json() as unknown[];
        const list = Array.isArray(arr) ? arr : [];
        const out: TradeActivity[] = list.map((t: any) => ({
          id: t.transactionHash || `${t.asset || ''}-${t.timestamp}`,
          asset_id: t.asset,
          side: t.side,
          size: t.size,
          price: t.price,
          timestamp: t.timestamp,
          outcome: t.outcome,
          username: (t.name && String(t.name)) || (t.pseudonym && String(t.pseudonym)) || '',
          address: t.proxyWallet,
        }));
        return { ok: true, data: out };
      } catch (e) { const error = e as Error; return err(error?.message ?? String(e)); }
    },
    async placeOrder(order: any) {
      return apiPost('/place-order', order);
    },
    async getFunds() {
      const resp = await apiGet<{ balance: string; allowance?: string }>('/funds');
      if (resp.ok && resp.data && (resp as any).data.allowance == null) {
        (resp as any).data.allowance = '0';
      }
      return resp as any;
    },
    async refreshFunds() {
      // Same as getFunds for now; backend can add refresh endpoint later
      const resp = await apiGet<{ balance: string; allowance?: string }>('/funds');
      if (resp.ok && resp.data && (resp as any).data.allowance == null) {
        (resp as any).data.allowance = '0';
      }
      return resp as any;
    },
    async listOpenOrders(filters?: { asset_ids?: string[] }) {
      const ids = (filters && Array.isArray(filters.asset_ids) && filters.asset_ids.length) ? filters.asset_ids.join(',') : '';
      const q = ids ? `?asset_ids=${encodeURIComponent(ids)}` : '';
      return apiGet<OpenOrder[]>(`/open-orders${q}`) as any;
    },
    async cancelOrder(orderId: string) {
      return apiPost('/cancel-order', { orderId });
    },
    async cancelAll(scope?: { asset_id?: string }) {
      return apiPost('/cancel-all', scope || {});
    },
    async getPositions(filters?: { asset_ids?: string[] }) {
      const ids = (filters && Array.isArray(filters.asset_ids) && filters.asset_ids.length) ? filters.asset_ids.join(',') : '';
      const q = ids ? `?asset_ids=${encodeURIComponent(ids)}` : '';
      return apiGet<Position[]>(`/positions${q}`) as any;
    },
    log() { /* no-op */ },
  } as unknown as PolymarketBridge;
}
